import React, { useState } from "react";
import styled from "styled-components";
import { Container, Section, Eyebrow } from "../components/ui/Container";
import { PageHeader } from "../components/ui/PageHeader";
import { Button, ExternalButton } from "../components/ui/Button";
import { colors, fonts, media, radius } from "../theme";

const channels = [
  { label: "General enquiries", value: "info@everpesa.com", href: "mailto:info@everpesa.com" },
  { label: "Sales, partnerships and investor relations", value: "sales@everpesa.com", href: "mailto:sales@everpesa.com" },
];

export const Contact = () => {
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", topic: "general", message: "" });

  const onSubmit = (e) => {
    e.preventDefault();
    const subject = encodeURIComponent(`[${form.topic}] ${form.name}`);
    const body = encodeURIComponent(`${form.message}\n\nFrom: ${form.name} <${form.email}>`);
    const to = form.topic === "invest_company" || form.topic === "partner" || form.topic === "software"
      ? "sales@everpesa.com"
      : "info@everpesa.com";
    window.location.href = `mailto:${to}?subject=${subject}&body=${body}`;
    setSubmitted(true);
  };

  return (
    <>
      <PageHeader
        eyebrow="Contact"
        title="Talk to a real person at Everpesa."
        sub="Whether you want to invest on the platform, back the company itself, apply for a loan, partner with us, or hire our team to build something, we would love to hear from you."
      />

      <Section>
        <Container>
          <Grid>
            <Form onSubmit={onSubmit}>
              <Field>
                <label>Your name</label>
                <input
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="John Doe"
                />
              </Field>
              <Field>
                <label>Email</label>
                <input
                  required
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="you@example.com"
                />
              </Field>
              <Field>
                <label>Topic</label>
                <select
                  value={form.topic}
                  onChange={(e) => setForm({ ...form, topic: e.target.value })}
                >
                  <option value="general">General enquiry</option>
                  <option value="invest">I want to invest on the platform</option>
                  <option value="invest_company">I want to invest in Everpesa</option>
                  <option value="borrow">I want to borrow</option>
                  <option value="partner">Partnership</option>
                  <option value="press">Press or media</option>
                  <option value="software">Software and ICT services</option>
                </select>
              </Field>
              <Field>
                <label>Message</label>
                <textarea
                  required
                  rows={5}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  placeholder="Tell us a little about what you're looking for…"
                />
              </Field>
              <Button type="submit" variant="primary">
                {submitted ? "Email opened ✓" : "Send message"}
              </Button>
              <Note>By submitting you agree to our terms and privacy policy.</Note>
            </Form>

            <Aside>
              <Eyebrow>Direct channels</Eyebrow>
              <h3 style={{ marginTop: 14, fontFamily: fonts.display }}>
                Email the right team directly.
              </h3>
              <List>
                {channels.map((c) => (
                  <Channel key={c.label} href={c.href}>
                    <span>{c.label}</span>
                    <strong>{c.value}</strong>
                  </Channel>
                ))}
              </List>

              <Office>
                <Eyebrow>Visit us</Eyebrow>
                <h4>Kampala HQ</h4>
                <address>
                  Everpesa Technologies Ltd<br />
                  National ICT Innovation Hub<br />
                  Plot 19 to 21 Portbell Road, Nakawa<br />
                  Kampala, Uganda
                </address>
                <Phones>
                  <a href="tel:+256786493685">+256 786 493 685</a>
                  <a href="tel:+256770703204">+256 770 703 204</a>
                </Phones>
                <ExternalButton
                  variant="outline"
                  size="sm"
                  href="https://invest.everpesa.com"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open the platform
                </ExternalButton>
              </Office>

              <Invest>
                <Eyebrow color="#9A6700">Invest in Everpesa</Eyebrow>
                <h4>Backing the business itself.</h4>
                <p>
                  We work with strategic and institutional investors interested in
                  backing Everpesa. Reach out and we will share an updated
                  investor pack and arrange a conversation.
                </p>
                <ExternalButton
                  variant="gold"
                  size="sm"
                  href="mailto:sales@everpesa.com?subject=Investing%20in%20Everpesa"
                >
                  sales@everpesa.com
                </ExternalButton>
              </Invest>
            </Aside>
          </Grid>
        </Container>
      </Section>
    </>
  );
};

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1.4fr 1fr;
  gap: 56px;
  ${media.md} { grid-template-columns: 1fr; gap: 40px; }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 18px;
  background: white;
  border: 1px solid ${colors.line};
  border-radius: ${radius.xl};
  padding: 36px;
  ${media.sm} { padding: 24px; }

  button { align-self: flex-start; }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  label {
    font-size: 13px;
    font-weight: 600;
    color: ${colors.inkSoft};
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  input, select, textarea {
    width: 100%;
    padding: 14px 16px;
    background: ${colors.surfaceAlt};
    border: 1px solid ${colors.line};
    border-radius: ${radius.md};
    font-family: inherit;
    font-size: 15px;
    color: ${colors.ink};
    transition: border-color 200ms ease, background 200ms ease;
    resize: vertical;

    &:focus {
      outline: none;
      border-color: ${colors.brand};
      background: white;
      box-shadow: 0 0 0 4px rgba(0, 51, 204, 0.1);
    }
  }
`;

const Note = styled.span`
  font-size: 12.5px;
  color: ${colors.inkSoft};
`;

const Aside = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Channel = styled.a`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 16px 18px;
  border: 1px solid ${colors.line};
  border-radius: ${radius.md};
  background: white;
  transition: border-color 200ms ease, transform 200ms ease;

  &:hover { border-color: ${colors.brand}; transform: translateY(-2px); }

  span { font-size: 12.5px; color: ${colors.inkSoft}; }
  strong { font-family: ${fonts.body}; font-weight: 600; color: ${colors.ink}; }
`;

const Office = styled.div`
  margin-top: 16px;
  padding: 24px;
  background: ${colors.surfaceAlt};
  border-radius: ${radius.lg};
  display: flex;
  flex-direction: column;
  gap: 12px;

  h4 { font-family: ${fonts.display}; font-size: 18px; margin-top: 4px; }
  address { font-style: normal; font-size: 14.5px; color: ${colors.inkMuted}; line-height: 1.7; }
  > a { align-self: flex-start; }
`;

const Phones = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 14.5px;
  font-weight: 600;
  color: ${colors.ink};
  a { color: ${colors.ink}; }
  a:hover { color: ${colors.brand}; }
`;

const Invest = styled.div`
  padding: 24px;
  background: ${colors.goldSoft};
  border: 1px solid #f5d791;
  border-radius: ${radius.lg};
  display: flex;
  flex-direction: column;
  gap: 12px;

  h4 { font-family: ${fonts.display}; font-size: 18px; margin-top: 4px; color: #5a3d00; }
  p { font-size: 14px; color: #5a3d00; line-height: 1.6; }
  > a { align-self: flex-start; }
`;
